import { useCallback, useMemo } from 'react';
import { createSearchParams } from 'react-router';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';

import type { AiConfigFilter } from '../helpers';
import {
  AI_CONFIG_LIST_CACHE_KEY,
  hasActiveFilters,
  parseEnvironmentAwareUISortParamsFromBrowser,
  serializeUIAiConfigFilterForBrowser,
} from '../helpers';

export function useCachedAiConfigListFilters() {
  const [rawParams, slot] = useStorageSlot(AI_CONFIG_LIST_CACHE_KEY, {
    storageType: 'sessionStorage',
    isEnabled: true,
  });

  const cached = useMemo(() => {
    if (!rawParams) {
      return {};
    }

    const params = createSearchParams(rawParams);
    return parseEnvironmentAwareUISortParamsFromBrowser(params);
  }, [rawParams]);

  const put = useCallback(
    (value?: AiConfigFilter) => {
      if (!value) {
        slot.remove();
        return;
      }

      if (!hasActiveFilters(value)) {
        slot.remove();
        return;
      }

      const aiConfigFilters = structuredClone(value);

      slot.set(serializeUIAiConfigFilterForBrowser(aiConfigFilters).toString());
    },
    [rawParams],
  );

  const value = useMemo(
    () => ({
      value: cached,
      put,
    }),
    [cached, put],
  );

  return value;
}
